"use strict";
/**
 * Pure helpers for building SignalHire searchByQuery bodies.
 * Shared by the API's SignalHireService and the worker's discovery search client.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.splitAndQuoteTerms = splitAndQuoteTerms;
exports.sanitizeBooleanQuery = sanitizeBooleanQuery;
exports.sanitizeLocations = sanitizeLocations;
exports.buildDiscoverySearchBody = buildDiscoverySearchBody;
/**
 * Split free-form title/company/skill items on OR/AND and re-quote each term.
 * "(Software AND Engineer) OR Developer" → ['"Software"', '"Engineer"', '"Developer"']
 */
function splitAndQuoteTerms(items) {
    const terms = [];
    for (const item of items) {
        // Remove surrounding parentheses and quotes, then split by OR/AND operators
        const cleaned = item.replace(/^\(|\)$/g, '').trim();
        const parts = cleaned.split(/\s+(?:OR|AND)\s+/i);
        for (const part of parts) {
            const trimmed = part.replace(/"/g, '').trim();
            if (trimmed) {
                terms.push(`"${trimmed}"`);
            }
        }
    }
    return terms;
}
/**
 * Sanitize a boolean query string for use in SignalHire API fields.
 * - Removes dangling operators (NOT, OR, AND) at start/end
 * - Optionally strips all NOT clauses (for inclusion-only fields like currentPastCompany)
 * - Collapses double/triple operators left after removal
 */
function sanitizeBooleanQuery(query, options) {
    if (!query || !query.trim())
        return '';
    let cleaned = query.trim();
    // If stripNot, remove all NOT clauses: "NOT term", "NOT \"quoted term\""
    if (options?.stripNot) {
        cleaned = cleaned.replace(/\bNOT\s+("(?:[^"\\]|\\.)*"|\S+)/gi, '').trim();
        // Also remove standalone NOT at the end/start (no argument)
        cleaned = cleaned.replace(/\bNOT\b\s*/gi, '').trim();
    }
    // Remove dangling operators at start
    cleaned = cleaned.replace(/^(\s*(AND|OR|NOT)\s*)+/gi, '').trim();
    // Remove dangling operators at end
    cleaned = cleaned.replace(/(\s*(AND|OR|NOT)\s*)+$/gi, '').trim();
    // Collapse double operators left after NOT removal (e.g., "A OR OR B" → "A OR B")
    cleaned = cleaned.replace(/\b(AND|OR)\s+(AND|OR)\b/gi, '$1').trim();
    // Remove extra whitespace
    cleaned = cleaned.replace(/\s+/g, ' ').trim();
    return cleaned;
}
/**
 * Sanitize location values into a flat array of plain strings.
 * AI may produce "Indonesia OR Malaysia" or "(Jakarta, Singapore)" — split and clean them.
 */
function sanitizeLocations(locations) {
    const result = [];
    for (const loc of locations) {
        // Split on OR / AND / , (but not commas inside a single city-country like "Jakarta, Indonesia")
        const parts = loc
            .replace(/[()]/g, '') // remove parentheses
            .split(/\s+(?:OR|AND)\s+/i) // split on OR / AND
            .map(p => p.trim())
            .filter(Boolean);
        result.push(...parts);
    }
    return result;
}
/**
 * Build a searchByQuery body from one discovery tier search.
 * currentPastCompany is inclusion-only on SignalHire, so excluded companies
 * ride along in keywords as NOT clauses (kept even in relaxed mode).
 */
function buildDiscoverySearchBody(search, opts) {
    const title = sanitizeBooleanQuery(search.currentTitle);
    const company = search.currentPastCompany
        ? sanitizeBooleanQuery(search.currentPastCompany, { stripNot: true })
        : '';
    const skillQuery = !opts.relaxed && search.keywords ? sanitizeBooleanQuery(search.keywords) : '';
    const notClauses = opts.excludedCompanies.map(c => `NOT "${c.replace(/"/g, '')}"`).join(' ');
    // A pure-negation keywords query is never sent: sanitizing would strip the
    // leading NOT and turn the exclusion into a requirement. Without a positive
    // part, exclusions are enforced by the caller's post-filter instead.
    const keywords = skillQuery
        ? (notClauses ? `(${skillQuery}) AND ${notClauses}` : `(${skillQuery})`)
        : '';
    const body = {
        [opts.relaxed ? 'currentPastTitle' : 'currentTitle']: title || undefined,
        currentPastCompany: company || undefined,
        location: !opts.dropLocation && search.location.length > 0 ? sanitizeLocations(search.location) : undefined,
        keywords: keywords || undefined,
        size: opts.size
    };
    if (!opts.relaxed) {
        if (search.yearsFrom !== null && search.yearsFrom > 0) {
            body.yearsOfCurrentPastExperienceFrom = search.yearsFrom;
        }
        if (search.yearsTo !== null && search.yearsTo > 0) {
            body.yearsOfCurrentPastExperienceTo = search.yearsTo;
        }
    }
    if (search.openToWork === true) {
        body.openToWork = true;
    }
    return Object.fromEntries(Object.entries(body).filter(([, v]) => v !== undefined && v !== ''));
}
//# sourceMappingURL=signalhire-query.js.map